import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useAuth } from "#app/lib/auth-context";
import { useWorkspace } from "#app/lib/data/app-provider";
import {
  FeatureFlags,
  FeatureFlagKey,
  FEATURE_FLAG_DEFINITION_LIST,
} from "@salestrak/feature-flags";
import { FEATURE_FLAGS_GRAPHQL_URL as FLAGS_API_URL } from "#app/lib/api-bases";

// The resolve in flight for a workspace, shared by every consumer. `hasFlag`
// is read from leaf components — `EntityVisual` mounts once per avatar — so a
// board paints dozens of hooks in the same tick, and each would otherwise
// fetch its own access token and its own copy of the flag set.
let pendingResolve: { workspaceId: string | null; promise: Promise<void> } | null =
  null;

function resolveFlags(
  workspaceId: string | null,
  getAccessToken: () => Promise<string | null>,
): Promise<void> {
  if (pendingResolve?.workspaceId === workspaceId) return pendingResolve.promise;

  const promise = (async () => {
    const token = await getAccessToken();
    // No token means no resolve: the set stays unresolved and `hasFlag`
    // answers false, the same as before any resolve has run.
    if (!token) return;
    // Don't reset() first — init() replaces the resolved set atomically, so
    // the previous workspace's flags stay readable until the new ones land
    // (no flag-gated UI flicker mid-switch).
    await FeatureFlags.init(token, { apiUrl: FLAGS_API_URL, workspaceId });
  })();

  pendingResolve = { workspaceId, promise };
  void promise
    .catch(() => {}) // every caller handles its own rejection
    .finally(() => {
      if (pendingResolve?.promise === promise) pendingResolve = null;
    });
  return promise;
}

// The reader `hasFlag` asks. A live reload swaps in a fresh function, so its
// identity marks each resolved set: `hasFlag` depends on it, and every
// consumer re-renders against the new flags.
const makeFlagReader = () => (key: FeatureFlagKey) =>
  FeatureFlags.isFeatureFlagEnabled(key);
let flagReader = makeFlagReader();
const flagsListeners = new Set<() => void>();
function subscribeFlags(listener: () => void): () => void {
  flagsListeners.add(listener);
  return () => {
    flagsListeners.delete(listener);
  };
}
const readFlagReader = () => flagReader;

/**
 * Resolve the flags again after a `featureFlags` workspace change. Drops any
 * resolve in flight first: it may have started before the write and would
 * answer with the old set.
 */
export async function reloadFeatureFlags(
  workspaceId: string | null,
  getAccessToken: () => Promise<string | null>,
): Promise<void> {
  pendingResolve = null;
  await resolveFlags(workspaceId, getAccessToken);
  flagReader = makeFlagReader();
  for (const listener of flagsListeners) listener();
}

export function useFeatureFlags() {
  const { getAccessToken } = useAuth();
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? null;
  const readFlag = useSyncExternalStore(subscribeFlags, readFlagReader);
  // Seed from the singleton, not `false`: the client is a module-level singleton
  // shared across every mount, so once it has resolved a set, a remounting
  // consumer is already ready and must not flash a loading state.
  const [ready, setReady] = useState(
    FeatureFlags.initialized && FeatureFlags.loadedWorkspaceId === workspaceId,
  );

  useEffect(() => {
    // The singleton tracks which workspace its flags were resolved against, so
    // re-init only when that's stale (first load or a workspace switch) — not on
    // every remount, which a per-instance ref would have forced.
    if (FeatureFlags.initialized && FeatureFlags.loadedWorkspaceId === workspaceId) {
      setReady(true);
      return;
    }

    // Stale set (first load or workspace switch): drop `ready` synchronously so no
    // flag-gated UI paints the previous workspace's flags while the new set
    // resolves. The component never remounts on a switch, so without this `ready`
    // stays true and `hasFlag` reads the prior workspace.
    setReady(false);

    let cancelled = false;

    async function init() {
      try {
        await resolveFlags(workspaceId, getAccessToken);
        if (cancelled) return;
        // A resolve that found no access token leaves the set unresolved.
        setReady(
          FeatureFlags.initialized &&
            FeatureFlags.loadedWorkspaceId === workspaceId,
        );
      } catch (err) {
        console.error("[FeatureFlags] Failed to initialize:", err);
        if (!cancelled) setReady(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken, workspaceId]);

  // Stable per workspace and flag set so consumers can safely list it in hook
  // deps — it reads the singleton at call time, and a live reload's new
  // `readFlag` renews its identity so memoized consumers recompute.
  const hasFlag = useCallback(
    (key: FeatureFlagKey): boolean => {
      // Gate on the active workspace, not just "ever initialized": after a switch the
      // singleton still holds the previous workspace's flags until the new resolve
      // lands, so a bare `initialized` check would leak them.
      if (!FeatureFlags.initialized || FeatureFlags.loadedWorkspaceId !== workspaceId) {
        return false;
      }
      return readFlag(key);
    },
    [workspaceId, readFlag],
  );

  return { ready, hasFlag };
}

export { FeatureFlagKey, FEATURE_FLAG_DEFINITION_LIST };
export type { FeatureFlagDefinition } from "@salestrak/feature-flags";
